import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  LoyaltyModelDetailDTO,
  LoyaltyModelEntity,
  LoyaltyOfferDTO,
  LoyaltyOffersInDTO,
  LoyaltyProduct,
  Multiplicator,
} from '../type';

export interface LoyaltyState {
  ids: string[];
  entity?: LoyaltyModelEntity;
  isLoading: boolean;
  isOffersLoading: boolean;
  loyaltyDialog: {
    type: 'new' | 'edit';
    isOpen: boolean;
    isLoading: boolean;
    data?: LoyaltyModelEntity | LoyaltyOfferDTO;
    isOfferDialog: 'offer' | 'card' | 'both';
  };
  confirmDeleteDialog: {
    id?: string;
    isOpen: boolean;
  };
  multiplicators: Multiplicator[];
}

const initialState: LoyaltyState = {
  ids: [],
  entity: undefined,
  isLoading: false,
  isOffersLoading: false,
  loyaltyDialog: {
    type: 'new',
    isOpen: false,
    data: undefined,
    isOfferDialog: 'both',
    isLoading: false,
  },
  confirmDeleteDialog: {
    id: undefined,
    isOpen: false,
  },
  multiplicators: [],
};

type PayloadProps = {
  data: LoyaltyModelEntity | LoyaltyOfferDTO;
  dialogType: 'offer' | 'card' | 'both';
};

export const { actions, reducer } = createSlice({
  name: 'loyaltiesApp',
  initialState,
  reducers: {
    setIsLoading: (state, action: { payload: boolean }) => {
      const isLoading = action.payload;
      state.isLoading = isLoading;
    },
    setIsOffersLoading: (state, action: { payload: boolean }) => {
      state.isOffersLoading = action.payload;
    },
    getLoyaltyModelSuccess: (state, action: PayloadAction<LoyaltyModelDetailDTO>) => {
      if (state.entity) {
        state.entity = {
          ...state.entity,
          ...action.payload,
        };
      } else {
        state.entity = {
          ...action.payload,
          loyaltyOffers: [],
          posProductIds: [],
        };
      }
    },
    patchLoyaltyProductList: (state, action: PayloadAction<LoyaltyProduct[]>) => {
      if (state.entity) {
        state.entity.selectedProductList = action.payload;
      }
    },
    getLoyaltyModelFailed: state => {
      state.entity = undefined;
    },
    getLoyaltyOffersSuccess: (state, action: PayloadAction<LoyaltyOffersInDTO>) => {
      if (state.entity) {
        state.entity.loyaltyOffers = action.payload.loyaltyOffers;
      }
    },
    getLoyaltyOffersFailed: state => {
      if (state.entity) {
        state.entity.loyaltyOffers = [];
      }
    },
    getLoyaltiesSuccess: (
      state,
      action: PayloadAction<{ loyaltyModel: LoyaltyModelDetailDTO; loyaltyOffers: LoyaltyOffersInDTO }>,
    ) => {
      const { loyaltyModel, loyaltyOffers } = action.payload;
      state.entity = {
        ...loyaltyModel,
        loyaltyOffers: loyaltyOffers.loyaltyOffers,
        posProductIds: state.entity?.posProductIds || [],
      };
    },
    getLoyaltiesFailed: state => {
      state.entity = undefined;
    },
    openNewLoyaltyDialog: (state, action: PayloadAction<'offer' | 'card' | 'both'>) => {
      state.loyaltyDialog = {
        type: 'new',
        isOpen: true,
        isOfferDialog: action.payload,
        isLoading: false,
      };
    },
    openEditLoyaltyDialog: (state, action: PayloadAction<PayloadProps>) => {
      state.loyaltyDialog = {
        type: 'edit',
        isOpen: true,
        data: action.payload.data,
        isOfferDialog: action.payload.dialogType,
        isLoading: false,
      };
    },
    setLoyaltyDialogIsLoading: (state, action) => {
      state.loyaltyDialog.isLoading = action.payload;
    },
    closeLoyaltyDialog: state => {
      state.loyaltyDialog.type = 'new';
      state.loyaltyDialog.isOpen = false;
      state.loyaltyDialog.isLoading = false;
    },
    openConfirmDeleteDialog: (state, action) => {
      state.confirmDeleteDialog = {
        id: action.payload.id,
        isOpen: true,
      };
    },
    closeConfirmDeleteDialog: state => {
      state.confirmDeleteDialog = {
        id: undefined,
        isOpen: false,
      };
    },
    updateLoyaltyOffer: (state, action: PayloadAction<LoyaltyOfferDTO>) => {
      const updatedOffer = action.payload;
      const offers = state.entity?.loyaltyOffers;
      if (offers) {
        const index = offers.findIndex(offer => offer.id === updatedOffer.id);
        if (index !== -1) {
          offers[index] = { ...offers[index], ...updatedOffer };
        }
      }
    },
    addOffer: (state, action: PayloadAction<LoyaltyOfferDTO>) => {
      const offers = state.entity?.loyaltyOffers;
      if (offers) {
        const newOffer = action.payload;
        newOffer.redeemedCount = 0; // This line will be removed when the backend will return the redeemedCount (when we will migrate the createOffer to APIV2)
        offers.push(action.payload);
      }
    },
    deleteLoyaltyOffer: (
      state,
      action: PayloadAction<{ loyaltyModelId: LoyaltyModelEntity['id']; offerId?: LoyaltyModelEntity['id'] }>,
    ) => {
      if (!state.entity?.loyaltyOffers) return;
      const newOffer = state.entity.loyaltyOffers.filter(offer => offer.id !== action.payload.offerId);
      state.entity.loyaltyOffers = newOffer;
    },
    getLoyaltyProductList: (state, action: PayloadAction<LoyaltyProduct[]>) => {
      if (state.entity) {
        state.entity.selectedProductList = action.payload;
      }
    },
    getLoyaltyExtrasList: (state, action: PayloadAction<LoyaltyProduct[]>) => {
      if (state.entity) {
        state.entity.selectedExtrasList = action.payload;
      }
    },
    resetLoyaltySlice: () => initialState,
  },
});
